import React, { useState, useEffect, useContext } from "react"
import { useTranslation } from "react-i18next"
import { compose } from "recompose"
import PropTypes from "prop-types"
import moment from "moment"

import {
  ChakraProvider,
  Box,
  Select,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Text,
  HStack,
  Spinner,
  Container,
  Heading,
  Stack,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Input,
} from "@chakra-ui/react"
import { Edit } from "@styled-icons/material/Edit"
import { Delete } from "@styled-icons/material/Delete"
import DatePicker from "react-datepicker"
import { useToasts } from "react-toast-notifications"

import { withFirebase } from "../../utils/Firebase"
import Nav from "../../components/atoms/Nav"
import { AuthUserContext, withAuthorization } from "../../context/Session"
import "react-datepicker/dist/react-datepicker.css"

const MIN_DURATION_MS = 5 * 24 * 60 * 60 * 1000 // 5 days

const isValidDateRange = (start, end) => {
  return end.diff(start) >= MIN_DURATION_MS
}

const mailerUrl = `${process.env.REACT_APP_FUNCTIONS_URL}/mailerSend`

const PilotPlan = ({ firebase }) => {
  const { t } = useTranslation()
  const authUser = useContext(AuthUserContext)
  const { addToast } = useToasts()

  const [selectedCompany, setSelectedCompany] = useState("")
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(moment().add(1, "months").toDate())
  const [loadingOrgs, setLoadingOrgs] = useState(true)
  const [orgsList, setOrgsList] = useState([])
  const [doctors, setDoctors] = useState([])
  const [editingOrg, setEditingOrg] = useState(null)
  const [editStartDate, setEditStartDate] = useState(new Date())
  const [editEndDate, setEditEndDate] = useState(
    moment().add(1, "months").toDate()
  )
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleteOrgId, setDeleteOrgId] = useState(null)
  const cancelRef = React.useRef()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const currentDate = Date.now()

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value)
    if (event.target.value) {
      const getDoctors = async () => {
        const orgDoctors = await firebase.getOrgDoctorList(event.target.value)
        setDoctors(orgDoctors)
      }
      getDoctors()
    }
  }

  useEffect(() => {
    setEndDate(moment(startDate).add(1, "months").toDate())
  }, [startDate])

  const getOrgs = async () => {
    setLoadingOrgs(true)
    const orgs = await firebase.getOrgs(null, authUser.country)
    if (orgs?.docs) {
      const sortedOrgs = orgs.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .sort((a, b) => a.businessName.localeCompare(b.businessName))
      setOrgsList(sortedOrgs)
    }
    setLoadingOrgs(false)
  }

  useEffect(() => {
    if (loadingOrgs && firebase) {
      getOrgs()
    }
  }, [loadingOrgs, firebase, authUser])

  const addTestPeriod = async () => {
    if (!selectedCompany) return

    const start = moment(startDate)
    const end = moment(endDate)

    if (!isValidDateRange(start, end)) {
      addToast(t("date_must_be_at_least_5_days"), { appearance: "error" })
      return
    }

    setLoadingOrgs(true)

    await firebase.updateOrgData({
      orgId: selectedCompany,
      orgData: {
        indexDocument: true,
        subscriptionPaid: true,
        subscriptionPaymentRetry: 0,
        nextPaymentDate: end.valueOf(),
        freeSubscriptionStartDate: start.valueOf(),
        freeSubscriptionEndDate: end.valueOf(),
        isTrialPlan: true,
        hasMessagingAccess: true,
        messagingNextPaymentDay: end.valueOf(),
      },
    })

    const orgDoctors = doctors.filter((d) => d.active)
    const promises = orgDoctors.map((doc) =>
      firebase.updateOrgDoctor({
        orgId: selectedCompany,
        id: doc.id,
        doctorData: {
          subscriptionPaid: true,
          subscriptionPaymentRetry: 0,
          active: true,
          nextPaymentDate: end.valueOf(),
        },
      })
    )

    await Promise.all(promises)

    const orgSnap = await firebase.org(selectedCompany).get()
    const orgData = orgSnap.data()

    if (orgData?.email) {
      const freePilotDays = moment(endDate).diff(moment(startDate), "days")
      const formattedStartDate = moment(startDate).format("DD/MM/YYYY")
      const formattedEndDate = moment(endDate).format("DD/MM/YYYY")
      const token = await firebase.getIdToken()

      fetch(mailerUrl, {
        method: "POST",
        body: JSON.stringify({
          data: {
            templateId: 7168419, // Business-Start-Free-Subscription
            email: orgData.email,
            bcc: [orgData.orgEmail],
            variables: {
              subject: "¡Su empresa ya forma parte del plan de prueba de DOC!",
              displayName: orgData.name,
              businessName: orgData.businessName,
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              freePilotDays,
            },
          },
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
    }

    addToast(t("test_period_added"), { appearance: "success" })

    getOrgs()
    setLoadingOrgs(false)
  }

  const confirmDeletePlan = (orgId) => {
    setDeleteOrgId(orgId)
    setIsDeleteOpen(true)
  }

  const deletePlan = async () => {
    await firebase.updateOrgData({
      orgId: deleteOrgId,
      orgData: {
        freeSubscriptionStartDate: null,
        freeSubscriptionEndDate: null,
        nextPaymentDate: null,
        subscriptionPaid: false,
        isTrialPlan: false,
        hasMessagingAccess: false,
        messagingNextPaymentDay: null,
      },
    })
    addToast(t("test_period_deleted"), { appearance: "info" })

    setIsDeleteOpen(false)
    getOrgs()
  }

  const openEditModal = (org) => {
    setEditingOrg(org)
    setEditStartDate(new Date(org.freeSubscriptionStartDate))
    setEditEndDate(new Date(org.freeSubscriptionEndDate))
    onOpen()
  }

  const handleEditPlan = async () => {
    if (!editingOrg) return

    const start = moment(editStartDate)
    const end = moment(editEndDate)

    if (!isValidDateRange(start, end)) {
      addToast(t("date_must_be_at_least_5_days"), { appearance: "error" })
      return
    }

    await firebase.updateOrgData({
      orgId: editingOrg.uid,
      orgData: {
        freeSubscriptionStartDate: start.valueOf(),
        freeSubscriptionEndDate: end.valueOf(),
        nextPaymentDate: end.valueOf(),
        isTrialPlan: true,
        messagingNextPaymentDay: end.valueOf(),
      },
    })

    addToast(t("test_period_updated"), { appearance: "success" })

    onClose()
    getOrgs()
  }

  const filteredOrgsWithFreePeriod = orgsList.filter(
    (item) => item.freeSubscriptionEndDate >= currentDate
  )

  return (
    <ChakraProvider>
      <Nav titleKey="pilot_plan" descriptionKey="pilot_plan_description" />
      <Container maxW="6xl" py={10}>
        {loadingOrgs ? (
          <VStack align="center" justify="center" height="60vh" width="100%">
            <Spinner size="xl" color="#4d66f5" />
          </VStack>
        ) : (
          <VStack spacing={10} align="start" w="100%" pb={200}>
            <Box w="100%" bg="gray.50" p={6} borderRadius="lg" boxShadow="sm">
              <FormControl>
                <FormLabel>{t("select_company")}</FormLabel>
                <Select
                  placeholder={t("select_company")}
                  onChange={handleCompanyChange}
                  cursor="pointer"
                >
                  {orgsList.map((org) => {
                    const isTrialActive =
                      org.freeSubscriptionEndDate &&
                      org.freeSubscriptionEndDate >= currentDate

                    return (
                      <option
                        key={org?.uid}
                        value={org?.uid}
                        disabled={isTrialActive}
                      >
                        {org?.businessName}{" "}
                        {isTrialActive ? `(${t("trial_active")})` : ""}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
              <HStack justifyContent="space-between" mt={9} mb={9}>
                <FormControl>
                  <FormLabel>{t("start_date")}</FormLabel>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="dd/MM/yyyy"
                    customInput={<Input cursor="pointer" />}
                  />
                </FormControl>
                <FormControl cursor="pointer">
                  <FormLabel>{t("end_date")}</FormLabel>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => {
                      if (moment(date).diff(moment(startDate), "days") >= 5) {
                        setEndDate(date)
                      } else {
                        addToast(t("date_must_be_at_least_5_days"), {
                          appearance: "warning",
                        })
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    customInput={<Input cursor="pointer" />}
                  />
                </FormControl>
              </HStack>

              <Button
                mt={4}
                colorScheme="blue"
                onClick={addTestPeriod}
                isDisabled={!selectedCompany}
              >
                {t("add_test_periods")}
              </Button>
            </Box>

            {filteredOrgsWithFreePeriod.length > 0 && (
              <Box w="100%">
                <Heading size="md" mb={4}>
                  {t("test_period_available")}:
                </Heading>
                <Stack spacing={3}>
                  {filteredOrgsWithFreePeriod.map((org) => (
                    <Box
                      key={org?.uid}
                      p={4}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="sm"
                    >
                      <HStack justifyContent="space-between">
                        <Box>
                          <Text fontWeight="semibold">
                            {org?.businessName}
                            {moment(org?.freeSubscriptionEndDate).diff(
                              moment(),
                              "days"
                            ) <= 5 && (
                              <Box as="span" ml={2}>
                                <Box
                                  as="span"
                                  px={2}
                                  py={0.5}
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="white"
                                  bg="red.500"
                                  borderRadius="md"
                                >
                                  {t("ending_soon")}
                                </Box>
                              </Box>
                            )}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {`${t("start_date")}: ${moment(
                              org?.freeSubscriptionStartDate
                            ).format("DD/MM/YYYY")}`}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {`${t("end_date")}: ${moment(
                              org?.freeSubscriptionEndDate
                            ).format("DD/MM/YYYY")}`}
                          </Text>
                        </Box>
                        <HStack>
                          <IconButton
                            aria-label="Edit Plan"
                            icon={<Edit size={20} />}
                            size="sm"
                            onClick={() => openEditModal(org)}
                          />
                          <IconButton
                            aria-label="Delete Plan"
                            icon={<Delete size={20} />}
                            size="sm"
                            colorScheme="red"
                            onClick={() => confirmDeletePlan(org?.uid)}
                          />
                        </HStack>
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </VStack>
        )}
      </Container>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("edit_test_period")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack justifyContent="space-between">
              <FormControl>
                <FormLabel>{t("start_date")}</FormLabel>
                <DatePicker
                  selected={editStartDate}
                  onChange={(date) => setEditStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  customInput={<Input cursor="pointer" />}
                />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>{t("end_date")}</FormLabel>
                <DatePicker
                  selected={editEndDate}
                  onChange={(date) => {
                    if (moment(date).diff(moment(editStartDate), "days") >= 5) {
                      setEditEndDate(date)
                    } else {
                      addToast(t("date_must_be_at_least_5_days"), {
                        appearance: "warning",
                      })
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  customInput={<Input cursor="pointer" />}
                />
              </FormControl>
            </HStack>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleEditPlan}>
              {t("save_button")}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t("cancel")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("delete_plan")}
            </AlertDialogHeader>

            <AlertDialogBody>{t("are_you_sure_delete")}</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteOpen(false)}>
                {t("cancel")}
              </Button>
              <Button colorScheme="red" onClick={deletePlan} ml={3}>
                {t("delete_button")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </ChakraProvider>
  )
}

PilotPlan.propTypes = {
  firebase: PropTypes.shape().isRequired,
}

const condition = (authUser) => !!authUser
const ComposedPilotPlan = compose(
  withFirebase,
  withAuthorization(condition)
)(PilotPlan)
export default ComposedPilotPlan
